/**
 * GuidePage — the PUBLIC testing-guide page (route `/guide`, OUTSIDE ProtectedRoute).
 *
 * Story: epic-uat-portal-001-13.
 *
 * Renders the full canonical tester guide ("How to test") so the portal is the
 * complete front door, not just walkthroughs + a form. The guide is fetched live
 * from the backend (single source of truth — the SPA never hardcodes a copy) and
 * rendered with react-markdown + remark-gfm (headings, tables, blockquotes, lists).
 *
 * SECURITY: `rehype-raw` is deliberately NOT used. react-markdown escapes raw HTML
 * by default, so any `<script>`/raw-HTML left in the payload renders as inert text,
 * never live markup — defence in depth even though the backend strips its internal
 * comments. We never render arbitrary HTML.
 *
 * Mirrors BoardPage's useState/useEffect/try-catch loading / error / empty / content
 * structure. The page is public — `fetchGuide` uses a plain fetch (no bearer) — so it
 * is reachable with no account and never redirects to /login.
 */

import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { fetchGuide, GuideApiError } from '@/services/guideService';
import styles from './GuidePage.module.css';

export function GuidePage() {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchGuide()
      .then((md) => {
        if (cancelled) return;
        setMarkdown(md);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof GuideApiError
            ? err.message
            : 'The testing guide is unavailable right now. Please try again shortly.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A blank guide (empty source, or whitespace-only) is treated like "unavailable":
  // there is nothing useful to render, so show the friendly state rather than a
  // blank page.
  const isEmpty = markdown !== null && markdown.trim().length === 0;

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-12" data-testid="guide-page">
      <div className="mb-6 text-sm">
        <Link to="/" data-testid="guide-back" className="underline">
          ← Back to the portal
        </Link>
      </div>

      <h1 className="mb-1 text-3xl font-bold" data-testid="guide-heading">
        How to test
      </h1>
      <p className="mb-8 text-sm text-muted-foreground">
        The full tester guide — what to try on each surface, how to try to break
        it, and what not to report.
      </p>

      {loading && (
        <p
          data-testid="guide-loading"
          className="py-8 text-center text-sm text-muted-foreground"
        >
          Loading the guide…
        </p>
      )}

      {error && (
        <div
          role="alert"
          data-testid="guide-error"
          className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {!loading && !error && isEmpty && (
        <div
          data-testid="guide-empty"
          className="rounded border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-muted-foreground"
        >
          The testing guide is unavailable right now. Please check back shortly.
        </div>
      )}

      {!loading && !error && markdown !== null && !isEmpty && (
        <article data-testid="guide-content" className={styles.guide}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </article>
      )}
    </main>
  );
}
